import { route } from 'preact-router';
import { useRef, useEffect } from 'preact/hooks';
import { renderPreview } from '../engine/renderer';
import type { Design } from '../types';

const DEMO_DESIGNS: Partial<Design>[] = [
  {
    text: '大好き',
    style: {
      preset: 'purupuru',
      fontFamily: 'Rounded Mplus 1c',
      textColor: '#FF6B9D',
      backgroundColor: '#FFFFFF',
      secondaryColor: '#FFB5D1',
      fontSize: 120,
      textOrientation: 'vertical',
      effects: [],
    },
    uchiwaType: 'jumbo',
  },
  {
    text: '最高',
    style: {
      preset: 'glitter',
      fontFamily: 'Noto Sans JP',
      textColor: '#FFD700',
      backgroundColor: '#1A1A2E',
      secondaryColor: '#FF6B9D',
      fontSize: 120,
      textOrientation: 'vertical',
      effects: [],
    },
    uchiwaType: 'jumbo',
  },
  {
    text: '推し',
    style: {
      preset: 'bold',
      fontFamily: 'Noto Sans JP',
      textColor: '#FF3D7F',
      backgroundColor: '#FFF5F8',
      secondaryColor: '#FFB5D1',
      fontSize: 120,
      textOrientation: 'vertical',
      effects: [],
    },
    uchiwaType: 'jumbo',
  },
];

function DemoCanvas({ design }: { design: Partial<Design> }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (canvasRef.current) {
      const fullDesign: Design = {
        id: 'demo',
        userId: null,
        createdAt: 0,
        updatedAt: 0,
        text: design.text ?? '',
        style: design.style as Design['style'],
        uchiwaType: design.uchiwaType ?? 'jumbo',
        isPublic: false,
        aiGenerated: false,
      };
      renderPreview(canvasRef.current, fullDesign);
    }
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        width: '140px',
        height: '135px',
        borderRadius: '14px',
        boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
      }}
    />
  );
}

export default function Home() {
  return (
    <div class="page-home">
      <section class="hero">
        <h1 class="hero-title">🪭 うちメーカー</h1>
        <p class="hero-subtitle">
          応援うちわ文字を、<br />かんたん・かわいく作ろう
        </p>
        <button
          class="btn-cta"
          onClick={() => route('/create')}
        >
          ✨ 無料で作り始める
        </button>
      </section>

      <section class="demo-section">
        <h2 class="demo-title">こんなうちわが作れます</h2>
        <div class="demo-grid">
          {DEMO_DESIGNS.map((d, i) => (
            <div key={i} class="demo-item">
              <DemoCanvas design={d} />
              <span class="demo-label">{d.style?.preset}</span>
            </div>
          ))}
        </div>
      </section>

      <section class="features">
        <div class="feature-card">
          <span class="feature-icon">🎨</span>
          <h3>4つのスタイル</h3>
          <p>シンプル・ボールド・ぷるぷる・グリッターから選べる</p>
        </div>
        <div class="feature-card">
          <span class="feature-icon">📝</span>
          <h3>縦書き・横書き</h3>
          <p>ワンタップで文字方向を切り替え</p>
        </div>
        <div class="feature-card">
          <span class="feature-icon">💾</span>
          <h3>ローカル保存</h3>
          <p>デザインをブラウザに保存、いつでも再編集</p>
        </div>
        <div class="feature-card">
          <span class="feature-icon">📱</span>
          <h3>スマホ対応</h3>
          <p>スマホでもPCでも快適に作成</p>
        </div>
      </section>

      <style>{`
        .page-home {
          min-height: calc(100vh - 52px);
          display: flex;
          flex-direction: column;
          align-items: center;
          background: linear-gradient(180deg, #FFF5F8 0%, #FFFFFF 60%);
          padding: 0 16px 48px;
        }
        .hero {
          text-align: center;
          padding: 48px 16px 32px;
          max-width: 480px;
        }
        .hero-title {
          font-size: 3rem;
          font-family: var(--font-display);
          color: var(--color-primary);
          margin: 0 0 12px;
        }
        .hero-subtitle {
          color: var(--color-text-secondary);
          font-size: 1.15rem;
          line-height: 1.6;
          margin: 0 0 28px;
        }
        .btn-cta {
          background: linear-gradient(135deg, var(--color-primary), var(--color-accent));
          color: white;
          border: none;
          border-radius: 9999px;
          padding: 14px 40px;
          font-size: 1.15rem;
          font-weight: bold;
          cursor: pointer;
          box-shadow: 0 4px 16px rgba(255, 107, 157, 0.35);
          transition: transform 0.15s, box-shadow 0.15s;
          font-family: inherit;
        }
        .btn-cta:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 24px rgba(255, 107, 157, 0.45);
        }
        .demo-section {
          text-align: center;
          margin: 32px 0;
        }
        .demo-title {
          font-size: 1.2rem;
          color: var(--color-text);
          margin: 0 0 20px;
        }
        .demo-grid {
          display: flex;
          gap: 20px;
          justify-content: center;
          flex-wrap: wrap;
        }
        .demo-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }
        .demo-label {
          font-size: 0.8rem;
          color: var(--color-text-secondary);
          font-weight: bold;
        }
        .features {
          display: flex;
          gap: 20px;
          margin-top: 40px;
          flex-wrap: wrap;
          justify-content: center;
          max-width: 800px;
        }
        .feature-card {
          background: white;
          border-radius: 16px;
          padding: 24px 20px;
          text-align: center;
          width: 170px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.06);
        }
        .feature-icon { font-size: 2rem; }
        .feature-card h3 {
          margin: 8px 0 4px;
          color: var(--color-text);
          font-size: 0.95rem;
        }
        .feature-card p {
          color: var(--color-text-secondary);
          font-size: 0.82rem;
          margin: 0;
          line-height: 1.4;
        }

        @media (max-width: 600px) {
          .hero-title { font-size: 2.2rem; }
          .features { gap: 12px; }
          .feature-card { width: 150px; padding: 16px; }
        }
      `}</style>
    </div>
  );
}
